<template>
  <div class="admin-top-banner-page">
    <div class="page-header">
      <h1 class="page-title">탑배너 관리</h1>
      <div class="header-actions">
        <button class="btn-add-job" @click="openJobSelectModal">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
          </svg>
          공고에서 추가
        </button>
        <button class="btn-add" @click="openAddModal">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          직접 추가
        </button>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
    </div>

    <div v-else-if="banners.length === 0" class="empty-state">
      <p>등록된 탑배너가 없습니다.</p>
    </div>

    <div v-else class="banner-list">
      <div v-for="(banner, index) in banners" :key="banner.id" class="banner-item">
        <div class="banner-order">
          <button @click="moveUp(index)" :disabled="index === 0">▲</button>
          <span>{{ index + 1 }}</span>
          <button @click="moveDown(index)" :disabled="index === banners.length - 1">▼</button>
        </div>
        
        <div class="banner-preview" :style="{ background: banner.bgColor }">
          <p>{{ banner.text }}</p>
        </div>
        
        <div class="banner-info">
          <div class="banner-meta">
            <span :class="['status', { active: banner.isActive }]">
              {{ banner.isActive ? '활성' : '비활성' }}
            </span>
            <span v-if="banner.jobId" class="job-badge">공고 연결</span>
          </div>
          <p v-if="banner.link" class="banner-link">{{ banner.link }}</p>
        </div>
        
        <div class="banner-actions">
          <button class="btn-edit" @click="openEditModal(banner)">수정</button>
          <button class="btn-toggle" @click="toggleActive(banner)">
            {{ banner.isActive ? '비활성화' : '활성화' }}
          </button>
          <button class="btn-delete" @click="handleDelete(banner)">삭제</button>
        </div>
      </div>
    </div>

    <!-- 배너 추가/수정 모달 -->
    <div v-if="showModal" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <h2 class="modal-title">{{ editingBanner ? '탑배너 수정' : '탑배너 추가' }}</h2>
        
        <div class="input-group">
          <label>배너 텍스트</label>
          <input type="text" v-model="formData.text" placeholder="🎉 신규 오픈 기념! 지금 가입하면 혜택이!" />
        </div>

        <div class="input-group">
          <label>링크 URL (선택)</label>
          <input type="text" v-model="formData.link" placeholder="/jobs/abc123 또는 https://..." />
        </div>

        <div class="input-group">
          <label>배경 색상</label>
          <div class="color-options">
            <label v-for="color in colorOptions" :key="color.value" class="color-option">
              <input type="radio" v-model="formData.bgColor" :value="color.value" />
              <span class="color-preview" :style="{ background: color.value }"></span>
              <span>{{ color.name }}</span>
            </label>
          </div>
        </div>

        <div class="input-group">
          <label>미리보기</label>
          <div class="preview-banner" :style="{ background: formData.bgColor }">
            <p>{{ formData.text || '배너 텍스트를 입력하세요' }}</p>
          </div>
        </div>

        <label class="checkbox-label">
          <input type="checkbox" v-model="formData.isActive" />
          <span>활성화</span>
        </label>

        <div class="modal-actions">
          <button class="btn-cancel" @click="closeModal">취소</button>
          <button class="btn-save" @click="saveBanner" :disabled="saving">
            {{ saving ? '저장 중...' : '저장' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 공고 선택 모달 -->
    <div v-if="showJobModal" class="modal-overlay" @click="showJobModal = false">
      <div class="modal-content job-modal" @click.stop>
        <h2 class="modal-title">공고에서 탑배너 추가</h2>
        
        <div class="search-bar">
          <input type="text" v-model="jobSearchQuery" placeholder="공고명 검색..." />
        </div>

        <div v-if="loadingJobs" class="loading-state small">
          <div class="spinner"></div>
        </div>

        <div v-else class="job-select-list">
          <div 
            v-for="job in filteredJobs" 
            :key="job.id" 
            class="job-select-item"
            @click="selectJobForBanner(job)"
          >
            <div class="job-select-info">
              <h4>{{ job.title }}</h4>
              <p>{{ job.siteName }} · {{ job.region }}</p>
            </div>
            <div class="job-select-action">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="12" y1="5" x2="12" y2="19"/>
                <line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="modal-actions">
          <button class="btn-cancel" @click="showJobModal = false">닫기</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query,
  orderBy,
  serverTimestamp 
} from 'firebase/firestore'
import { db } from '@/services/firebase'

export default {
  name: 'AdminTopBannerView',
  setup() {
    const banners = ref([])
    const jobs = ref([])
    const loading = ref(true)
    const loadingJobs = ref(false)
    const showModal = ref(false)
    const showJobModal = ref(false)
    const editingBanner = ref(null)
    const saving = ref(false)
    const jobSearchQuery = ref('')

    const formData = ref({
      text: '',
      link: '',
      bgColor: '#3182f6',
      isActive: true,
      jobId: null
    })

    const colorOptions = [
      { name: '블루', value: '#3182f6' },
      { name: '그린', value: '#12b886' },
      { name: '퍼플', value: '#7950f2' },
      { name: '오렌지', value: '#fd7e14' },
      { name: '레드', value: '#e03131' },
      { name: '다크', value: '#191f28' }
    ]

    const filteredJobs = computed(() => {
      if (!jobSearchQuery.value) return jobs.value
      const q = jobSearchQuery.value.toLowerCase()
      return jobs.value.filter(job => 
        job.title?.toLowerCase().includes(q) ||
        job.siteName?.toLowerCase().includes(q)
      )
    })

    const fetchBanners = async () => {
      loading.value = true
      try {
        const q = query(collection(db, 'topBanners'), orderBy('order', 'asc'))
        const snapshot = await getDocs(q)
        banners.value = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      } catch (e) {
        console.error('탑배너 로드 실패:', e)
      } finally {
        loading.value = false
      }
    }

    const fetchJobs = async () => {
      loadingJobs.value = true
      try {
        const q = query(collection(db, 'jobs'), orderBy('createdAt', 'desc'))
        const snapshot = await getDocs(q)
        jobs.value = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      } catch (e) {
        console.error('공고 로드 실패:', e)
      } finally {
        loadingJobs.value = false
      }
    }

    const openAddModal = () => {
      editingBanner.value = null
      formData.value = { text: '', link: '', bgColor: '#3182f6', isActive: true, jobId: null }
      showModal.value = true
    }

    const openEditModal = (banner) => {
      editingBanner.value = banner
      formData.value = {
        text: banner.text,
        link: banner.link || '',
        bgColor: banner.bgColor || '#3182f6',
        isActive: banner.isActive,
        jobId: banner.jobId || null
      }
      showModal.value = true
    }

    const openJobSelectModal = () => {
      jobSearchQuery.value = ''
      showJobModal.value = true
      if (jobs.value.length === 0) {
        fetchJobs()
      }
    }

    const selectJobForBanner = async (job) => {
      saving.value = true
      try {
        await addDoc(collection(db, 'topBanners'), {
          text: `🔥 ${job.title} - ${job.region} 모집중!`,
          link: `/jobs/${job.id}`,
          bgColor: '#3182f6',
          isActive: true,
          jobId: job.id,
          order: banners.value.length,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        })
        
        showJobModal.value = false
        fetchBanners()
        alert(`"${job.title}" 공고가 탑배너로 추가되었습니다.`)
      } catch (e) {
        console.error('탑배너 추가 실패:', e)
        alert('추가에 실패했습니다.')
      } finally {
        saving.value = false
      }
    }

    const closeModal = () => {
      showModal.value = false
      editingBanner.value = null
    }

    const saveBanner = async () => {
      if (!formData.value.text) {
        alert('배너 텍스트를 입력해주세요.')
        return
      }

      saving.value = true
      try {
        if (editingBanner.value) {
          await updateDoc(doc(db, 'topBanners', editingBanner.value.id), {
            text: formData.value.text,
            link: formData.value.link,
            bgColor: formData.value.bgColor,
            isActive: formData.value.isActive,
            updatedAt: serverTimestamp()
          })
        } else {
          await addDoc(collection(db, 'topBanners'), {
            text: formData.value.text,
            link: formData.value.link,
            bgColor: formData.value.bgColor,
            isActive: formData.value.isActive,
            jobId: null,
            order: banners.value.length,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          })
        }

        closeModal()
        fetchBanners()
      } catch (e) {
        console.error('탑배너 저장 실패:', e)
        alert('저장에 실패했습니다.')
      } finally {
        saving.value = false
      }
    }

    const toggleActive = async (banner) => {
      try {
        await updateDoc(doc(db, 'topBanners', banner.id), {
          isActive: !banner.isActive,
          updatedAt: serverTimestamp()
        })
        banner.isActive = !banner.isActive
      } catch (e) {
        console.error('상태 변경 실패:', e)
      }
    }

    const handleDelete = async (banner) => {
      if (!confirm('이 탑배너를 삭제하시겠습니까?')) return
      
      try {
        await deleteDoc(doc(db, 'topBanners', banner.id))
        fetchBanners()
      } catch (e) {
        console.error('삭제 실패:', e)
      }
    }

    const moveUp = async (index) => {
      if (index === 0) return
      const temp = banners.value[index]
      banners.value[index] = banners.value[index - 1]
      banners.value[index - 1] = temp
      await updateOrders()
    }

    const moveDown = async (index) => {
      if (index === banners.value.length - 1) return
      const temp = banners.value[index]
      banners.value[index] = banners.value[index + 1]
      banners.value[index + 1] = temp
      await updateOrders()
    }

    const updateOrders = async () => {
      try {
        for (let i = 0; i < banners.value.length; i++) {
          await updateDoc(doc(db, 'topBanners', banners.value[i].id), { order: i })
        }
      } catch (e) {
        console.error('순서 변경 실패:', e)
      }
    }

    onMounted(fetchBanners)

    return {
      banners,
      jobs,
      loading,
      loadingJobs,
      showModal,
      showJobModal,
      editingBanner,
      saving,
      formData,
      colorOptions,
      jobSearchQuery,
      filteredJobs,
      openAddModal,
      openEditModal,
      openJobSelectModal,
      selectJobForBanner,
      closeModal,
      saveBanner,
      toggleActive,
      handleDelete,
      moveUp,
      moveDown
    }
  }
}
</script>

<style scoped>
.admin-top-banner-page {
  padding: 40px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: #191f28;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.btn-add, .btn-add-job {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}

.btn-add {
  background: #3182f6;
  color: #fff;
}

.btn-add:hover {
  background: #1b64da;
}

.btn-add-job {
  background: #f0f7ff;
  color: #3182f6;
  border: 1px solid #3182f6;
}

.btn-add-job:hover {
  background: #e0efff;
}

.loading-state, .empty-state {
  text-align: center;
  padding: 60px;
  color: #6b7684;
}

.loading-state.small {
  padding: 40px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f2f4f6;
  border-top-color: #3182f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.banner-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.banner-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.banner-order {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.banner-order button {
  padding: 4px 8px;
  background: #f2f4f6;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.banner-order button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.banner-order span {
  font-size: 14px;
  font-weight: 600;
  color: #6b7684;
}

.banner-preview {
  flex: 1;
  padding: 12px 20px;
  border-radius: 8px;
  text-align: center;
}

.banner-preview p {
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  margin: 0;
}

.banner-info {
  min-width: 120px;
}

.banner-meta {
  display: flex;
  gap: 8px;
  margin-bottom: 4px;
}

.status {
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
  background: #f2f4f6;
  color: #6b7684;
}

.status.active {
  background: #dcfce7;
  color: #16a34a;
}

.job-badge {
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
  background: #f0f7ff;
  color: #3182f6;
}

.banner-link {
  font-size: 12px;
  color: #8b95a1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
}

.banner-actions {
  display: flex;
  gap: 8px;
}

.btn-edit, .btn-toggle, .btn-delete {
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  border-radius: 8px;
  cursor: pointer;
}

.btn-edit {
  background: #f2f4f6;
  color: #4e5968;
}

.btn-toggle {
  background: #f0f7ff;
  color: #3182f6;
}

.btn-delete {
  background: #fff5f5;
  color: #e03131;
}

/* 모달 */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 24px;
}

.modal-content {
  background: #fff;
  border-radius: 16px;
  padding: 32px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-content.job-modal {
  max-width: 600px;
}

.modal-title {
  font-size: 20px;
  font-weight: 700;
  color: #191f28;
  margin-bottom: 24px;
}

.input-group {
  margin-bottom: 20px;
}

.input-group label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.input-group input[type="text"] {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #e5e8eb;
  border-radius: 8px;
  font-size: 15px;
}

.color-options {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.color-option {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 2px solid #e5e8eb;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.15s;
}

.color-option input {
  display: none;
}

.color-option:has(input:checked) {
  border-color: #3182f6;
  background: #f0f7ff;
}

.color-preview {
  width: 20px;
  height: 20px;
  border-radius: 4px;
}

.preview-banner {
  padding: 12px 20px;
  border-radius: 8px;
  text-align: center;
}

.preview-banner p {
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  margin: 0;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  margin-bottom: 24px;
  cursor: pointer;
}

.modal-actions {
  display: flex;
  gap: 12px;
}

.btn-cancel, .btn-save {
  flex: 1;
  padding: 14px;
  font-size: 15px;
  font-weight: 600;
  border: none;
  border-radius: 10px;
  cursor: pointer;
}

.btn-cancel {
  background: #f2f4f6;
  color: #4e5968;
}

.btn-save {
  background: #3182f6;
  color: #fff;
}

.btn-save:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 공고 선택 */
.search-bar {
  margin-bottom: 20px;
}

.search-bar input {
  width: 100%;
  padding: 14px 16px;
  border: 1px solid #e5e8eb;
  border-radius: 10px;
  font-size: 15px;
}

.job-select-list {
  max-height: 400px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
}

.job-select-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px;
  background: #f9fafb;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s;
}

.job-select-item:hover {
  background: #f0f7ff;
}

.job-select-info {
  flex: 1;
  min-width: 0;
}

.job-select-info h4 {
  font-size: 14px;
  font-weight: 600;
  color: #191f28;
  margin-bottom: 4px;
}

.job-select-info p {
  font-size: 12px;
  color: #6b7684;
}

.job-select-action {
  width: 32px;
  height: 32px;
  background: #3182f6;
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 768px) {
  .admin-top-banner-page {
    padding: 24px 16px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .header-actions {
    width: 100%;
  }

  .btn-add, .btn-add-job {
    flex: 1;
  }

  .banner-item {
    flex-wrap: wrap;
  }

  .banner-preview {
    order: -1;
    width: 100%;
  }

  .banner-actions {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>